import { CheckCircle, RestartAlt } from "@mui/icons-material";
import {
  Box,
  Button,
  Collapse,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import Breadcrumb from "../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import InlineCode from "../App/Components/Typography/InlineCode";
import { useSaveUserProfileMutation, useTimezonesQuery } from "../App/Queries/UserProfileQueries";
import { useCurrentUser } from "../App/Queries/UserQueries";
import { useNotification } from "../App/State/Hooks/notificationHooks";
import { useUserProfileStore } from "../App/State/Stores/UserProfileStore";

const MAX_TITLE_LENGTH = 100;

function UserProfile() {
  const user = useCurrentUser();
  const { profile } = useUserProfileStore();
  const { data: timezones } = useTimezonesQuery();
  const { mutateAsync: saveProfileAsync, isLoading: isSaving } = useSaveUserProfileMutation();
  const [title, setTitle] = useState<string>(profile.title);
  const [timezone, setTimezone] = useState<string>(profile.timezoneId ?? "Eastern Standard Time");
  const [notifyOnEmailReply, setNotifyOnEmailReply] = useState<boolean>(profile.notifyOnEmailReply ?? false);
  const [notifyOnTextReply, setNotifyOnTextReply] = useState<boolean>(profile.notifyOnTextReply ?? false);
  const { displaySuccess, displayError } = useNotification();

  const hasExistingTitle = useMemo(() => Boolean(profile.title.trim().length > 0), [profile.title]);
  const isTitleEmpty = useMemo(() => title.trim().length === 0, [title]);
  const isSaveDisabled = useMemo(
    () => isSaving || title.length > MAX_TITLE_LENGTH || (hasExistingTitle && isTitleEmpty),
    [isSaving, title, hasExistingTitle, isTitleEmpty],
  );

  const handleSaveProfile = useCallback(async () => {
    // Check if title is too long, max 100 characters
    if (title.length > MAX_TITLE_LENGTH) {
      return;
    }
    // Check if user previously had a title but is trying to clear it
    if (hasExistingTitle && isTitleEmpty) {
      displayError("Title cannot be cleared once set.");
      return;
    }
    await saveProfileAsync(
      {
        userAuthId: user?.id ?? "",
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        title,
        timezoneId: timezone,
        notifyOnEmailReply,
        notifyOnTextReply,
      },
      {
        onSuccess: () => {
          displaySuccess("User profile updated.");
        },
        onError: () => {
          displayError("Failed to update user profile.");
        },
      },
    );
  }, [
    profile.firstName,
    profile.lastName,
    profile.email,
    title,
    timezone,
    notifyOnEmailReply,
    notifyOnTextReply,
    user?.id,
  ]);

  useEffect(() => {
    if (profile) {
      setTimezone(profile.timezoneId);
      setTitle(profile.title);
      setNotifyOnEmailReply(profile.notifyOnEmailReply ?? false);
      setNotifyOnTextReply(profile.notifyOnTextReply ?? false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.timezoneId, profile.title, profile.notifyOnEmailReply, profile.notifyOnTextReply]);

  return (
    <PageContainer>
      <Grid container>
        <Grid item xs={12}>
          <Breadcrumb pageTitle="User Profile" />
        </Grid>
      </Grid>
      <Paper sx={{ mt: { xs: 1, md: 2 }, p: { xs: 2, lg: 4 }, flex: 1 }}>
        <Grid container>
          <Grid item xs={12} sx={{ marginBottom: "10px" }}>
            <PageTitle sx={{ lineHeight: 1 }}>User Profile</PageTitle>
          </Grid>
          <Grid item xs={12} sm={12} md={6} xl={4}>
            <Stack direction="column" spacing={2}>
              <Table>
                <TableBody>
                  <TableRow data-test="profile-firstname">
                    <TableCell
                      variant="head"
                      sx={{
                        fontWeight: "bold",
                        maxWidth: "20em",
                        width: 0,
                        minWidth: "12em",
                        borderBottomWidth: "1px",
                      }}
                    >
                      First Name
                    </TableCell>
                    <TableCell sx={{ borderBottomWidth: "1px" }}>{profile.firstName}</TableCell>
                  </TableRow>
                  <TableRow data-test="profile-lastname">
                    <TableCell
                      variant="head"
                      sx={{
                        fontWeight: "bold",
                        maxWidth: "20em",
                        width: 0,
                        minWidth: "12em",
                        borderBottomWidth: "1px",
                      }}
                    >
                      Last Name
                    </TableCell>
                    <TableCell sx={{ borderBottomWidth: "1px" }}>{profile.lastName}</TableCell>
                  </TableRow>
                  <TableRow data-test="profile-email">
                    <TableCell
                      variant="head"
                      sx={{
                        fontWeight: "bold",
                        maxWidth: "20em",
                        width: 0,
                        minWidth: "12em",
                        borderBottomWidth: "1px",
                      }}
                    >
                      Email
                    </TableCell>
                    <TableCell sx={{ borderBottomWidth: "1px" }}>{profile.email}</TableCell>
                  </TableRow>
                  <TableRow data-test="profile-title">
                    <TableCell
                      variant="head"
                      sx={{
                        fontWeight: "bold",
                        maxWidth: "20em",
                        width: 0,
                        minWidth: "12em",
                        borderBottomWidth: "1px",
                      }}
                    >
                      Title
                    </TableCell>
                    <TableCell sx={{ borderBottomWidth: "1px" }}>
                      <TextField
                        id="title-input"
                        data-test="title-input"
                        aria-labelledby="profile-title-label"
                        fullWidth
                        value={title}
                        placeholder="e.g., Sales Manager"
                        onChange={(d) => {
                          setTitle(d.target.value);
                        }}
                        error={title.length > MAX_TITLE_LENGTH || (hasExistingTitle && isTitleEmpty)}
                        InputProps={{
                          endAdornment:
                            hasExistingTitle && isTitleEmpty ? (
                              <IconButton
                                data-test="reset-title-button"
                                onClick={() => setTitle(profile.title)}
                                size="small"
                                title="Reset Title"
                              >
                                <RestartAlt fontSize="small" />
                              </IconButton>
                            ) : null,
                        }}
                      />
                      <Collapse in={title.length > MAX_TITLE_LENGTH} timeout="auto" unmountOnExit>
                        <FormHelperText error={true} data-test="title-error">
                          Title is too long. Maximum length is {MAX_TITLE_LENGTH} characters.
                        </FormHelperText>
                      </Collapse>
                      <Collapse in={hasExistingTitle && isTitleEmpty} timeout="auto" unmountOnExit>
                        <FormHelperText error={true} data-test="title-empty-error">
                          Title cannot be cleared once set. Current title is: {` "${profile.title}"`}
                        </FormHelperText>
                      </Collapse>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                        <FormHelperText>
                          Setting this value enables the <InlineCode>[[RecruiterTitle]]</InlineCode> template
                          field in messaging.
                        </FormHelperText>
                        {hasExistingTitle && (
                          <CheckCircle data-test="title-checkmark" fontSize="small" color="success" />
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow data-test="profile-timezone">
                    <TableCell
                      id="profile-timezone-label"
                      variant="head"
                      sx={{
                        fontWeight: "bold",
                        maxWidth: "20em",
                        width: 0,
                        minWidth: "12em",
                        borderBottomWidth: 0,
                      }}
                    >
                      Timezone
                    </TableCell>
                    <TableCell sx={{ borderBottomWidth: 0 }}>
                      <TextField
                        id="timezone-selection"
                        data-test="timezone-dropdown"
                        aria-labelledby="profile-timezone-label"
                        fullWidth
                        select
                        value={timezone}
                        onChange={(d) => {
                          setTimezone(d.target.value);
                        }}
                      >
                        {timezones?.map((option) => (
                          <MenuItem key={option.id} value={option.id} data-test={`tz-option-${option.id}`}>
                            {option.displayName}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Notification Preferences
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        data-test="notify-on-email-reply-switch"
                        checked={notifyOnEmailReply}
                        onChange={(e) => setNotifyOnEmailReply(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Notify me when a candidate replies to an email I send"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        data-test="notify-on-text-reply-switch"
                        checked={notifyOnTextReply}
                        onChange={(e) => setNotifyOnTextReply(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Notify me when a candidate replies to a text message I send"
                  />
                </Stack>
                <FormHelperText sx={{ mt: 1 }}>
                  When enabled, you will automatically be subscribed to response notifications for all future
                  messages of that type. You can still override this setting when composing individual
                  messages.
                </FormHelperText>
              </Box>
              <Button
                onClick={handleSaveProfile}
                disabled={isSaveDisabled}
                data-test="save-profile-button"
                type="button"
                variant="contained"
                size="large"
              >
                Save
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </PageContainer>
  );
}

export default UserProfile;
